import { memberEventsTreeForDate } from './member_event_tree_utils'
import {
  getDoubleBookingEvents,
  getOverlappingEvents,
  isSecondaryOrCalendlyButNotHoliday,
  isSecondaryOrReservedButNotHoliday,
  timeRange
} from './utils'

const overlapsEvent = (event, { start, end }) => {
  const { start: eventStart, end: eventEnd } = event
  return !(start.isSameOrAfter(eventEnd) || end.isSameOrBefore(eventStart))
}

const overlappingEvents = (memberEvents, { start, end }) => memberEvents.filter((memberEvent) => overlapsEvent(memberEvent, { start, end }))

const spotIsOccupied = (selectedSpots, spotStart, spotEnd = null) => (
  selectedSpots.some(({ start, end }) => (
    spotEnd
      ? spotStart.isBetween(start, end, 'minute', '[)')
        || spotEnd.isBetween(start, end, 'minute', '(]')
      : spotStart.isSame(start)
  ))
)

const isSpotAvailable = (eventsOverlapping, allowEventsOverlap = true, allowInternalEventsOverlap = false) => {
  const filter = allowInternalEventsOverlap ? isSecondaryOrReservedButNotHoliday : isSecondaryOrCalendlyButNotHoliday

  return allowEventsOverlap
    ? !eventsOverlapping.some(filter)
    : eventsOverlapping.length === 0
}

const overlapsSchedule = (unavailabilityIntervals, { start, end }) => unavailabilityIntervals.some((interval) => {
  const { start: intervalStart, end: intervalEnd } = interval

  return !(start.isSameOrAfter(intervalEnd) || end.isSameOrBefore(intervalStart))
})

const buildAvailableSpot = (
  memberEvents,
  currentTime,
  start,
  end,
  unavailabilityIntervals,
  timezone,
  timeNotation,
  allowEventsOverlap = true,
  allowInternalEventsOverlap = false
) => {
  const eventsOverlapping = getOverlappingEvents(memberEvents, start, end)
  const doubleBookingEvents = getDoubleBookingEvents(eventsOverlapping, allowInternalEventsOverlap)
  const hasScheduleOverlap = overlapsSchedule(unavailabilityIntervals, { start, end })
  const hasEventOverlap = eventsOverlapping.length > 0
  const isExpired = start.unix() < currentTime
  const available = isSpotAvailable(eventsOverlapping, allowEventsOverlap, allowInternalEventsOverlap) && !isExpired

  return {
    display: 'background',
    title: '',
    start: start.toISOString(),
    end: end.toISOString(),
    overlap: false,
    extendedProps: {
      background: true,
      selectedSpot: false,
      timeRange: timeRange({ start, end, timezone, timeNotation }),
      disabled: false,
      available,
      hasEventOverlap,
      hasScheduleOverlap,
      doubleBookingEvents,
      timezone
    }
  }
}

const availableSpots = (
  currentDate,
  timezone,
  timeNotation,
  eventTypeDuration,
  selectedSpots,
  memberEvents,
  unavailabilityIntervals,
  spotStep = 15,
  allowEventsOverlap = true,
  allowSpotsOverlap = true,
  allowInternalEventsOverlap = false
) => {
  const currentTime = Math.floor(Date.now() / 1000)
  const startOfDay = currentDate.clone().startOf('day')
  const endOfDay = startOfDay.clone().add(1, 'day')
  const spots = []
  const memberEventsForCurrentDate = memberEventsTreeForDate(
    memberEvents,
    startOfDay,
    timezone
  )
  while (startOfDay.isBefore(endOfDay)) {
    const start = startOfDay.clone()
    const end = start.clone().add(eventTypeDuration, 'minutes')

    if (end.isSameOrBefore(endOfDay) && !spotIsOccupied(selectedSpots, start, allowSpotsOverlap ? null : end)) {
      spots.push(
        buildAvailableSpot(
          memberEventsForCurrentDate,
          currentTime,
          start,
          end,
          unavailabilityIntervals,
          timezone,
          timeNotation,
          allowEventsOverlap,
          allowInternalEventsOverlap
        )
      )
    }
    startOfDay.add(spotStep, 'minutes')
  }
  return spots
}


export {
  availableSpots,
  overlappingEvents,
  isSpotAvailable,
  spotIsOccupied
}
